/**
 * The Endless Palace orchestrator: loads the semantic clusters, builds the
 * deterministic plan, and streams the building around the visitor.
 *
 * Two independent radii do the heavy lifting:
 *  - chunks (rooms / spine segments / rotunda) are built inside LOAD_R and
 *    disposed beyond UNLOAD_R, at most one build per frame;
 *  - canvas textures are created inside TEX_R and dropped back to the shared
 *    linen placeholder beyond TEX_OUT, a couple per frame, scanned round-robin
 *    so thousands of hung quads never get iterated in a single tick.
 *
 * The hysteresis gap on both pairs stops churn at the boundary.
 */

import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { drawContentCanvas } from '../museumCanvas';
import { planPalaceSteps } from './palaceLayout';
import { createPalaceKit, disposePalaceKit, buildChunk, disposeChunk } from './palaceBuild';
import { mediaForContent, loadMedia, cachedMedia, clearMediaCache } from './media';

const TUNING = {
  desktop: {
    LOAD_R: 48, UNLOAD_R: 62, TEX_R: 26, TEX_OUT: 34,
    PIXEL_BUDGET: 350000, MEDIA_VARIANT: 'medium', LAMPS: 5,
  },
  mobile: {
    LOAD_R: 35, UNLOAD_R: 48, TEX_R: 16, TEX_OUT: 24,
    PIXEL_BUDGET: 180000, MEDIA_VARIANT: 'small', LAMPS: 2,
  },
};
const CHUNK_CHECK_EVERY = 8; // ticks between load/unload sweeps
const PICK_RANGE = 22; // metres: far enough to reach across a hall, not a wing
const TEX_SCAN_PER_TICK = 150; // quads examined per tick
const TEX_CREATE_PER_TICK = 2; // canvases painted per tick
const PLAN_SLICE_MS = 50; // plan work between paints, while the entry screen waits

// Hand the browser a frame: rAF gets us to the next paint, the task after it
// resumes once that paint is on screen.
const nextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => setTimeout(resolve, 0));
});

const PLAN_PHASES = {
  route: (s) => `Laying the spine — wing ${s.done} of ${s.total}`,
  grow: (s) => `Growing the wings — ${s.done} rooms`,
  rescue: (s) => `Annexing the overflow — wing ${s.done} of ${s.total}`,
  hang: (s) => `Hanging the walls — ${s.done.toLocaleString()} frames`,
};

// Drive the layout generator in slices, reporting where it is between them.
// Without this the tab freezes for seconds on the entry screen with a spinner
// that cannot even animate.
const runPlan = async (clusters, annex, report) => {
  const steps = planPalaceSteps(clusters, annex);
  let step = steps.next();
  let since = performance.now();
  while (!step.done) {
    if (performance.now() - since > PLAN_SLICE_MS) {
      report(PLAN_PHASES[step.value.phase]?.(step.value) || '');
      await nextPaint();
      since = performance.now();
    }
    step = steps.next();
  }
  return step.value;
};

const distToAabb = (x, z, aabb) => {
  const dx = Math.max(aabb.minX - x, 0, x - aabb.maxX);
  const dz = Math.max(aabb.minZ - z, 0, z - aabb.maxZ);
  return Math.sqrt(dx * dx + dz * dz);
};

// Join the analysis clusters against the live tweet list. Saves the analysis
// never saw (new since the last run, or unclustered) become the annex.
const joinClusters = (analysis, tweets) => {
  const byId = new Map(tweets.map((t) => [String(t.id), t]));
  const claimed = new Set();
  const clusters = [];

  const raw = analysis?.semantic_clusters?.clusters || {};
  for (const [id, cluster] of Object.entries(raw)) {
    if (id === '-1') continue;
    const members = [];
    for (const ref of cluster.tweet_refs || []) {
      const tweet = byId.get(String(ref.id));
      if (tweet && !claimed.has(String(ref.id))) {
        claimed.add(String(ref.id));
        members.push(tweet);
      }
    }
    clusters.push({
      id,
      label: cluster.topic_label || `Topic ${Number(id) + 1}`,
      terms: cluster.topic_terms || [],
      size: members.length,
      tweets: members,
    });
  }

  // A save can arrive twice (liked and bookmarked); hang each id once.
  const annex = [];
  for (const t of tweets) {
    const id = String(t.id);
    if (claimed.has(id)) continue;
    claimed.add(id);
    annex.push(t);
  }
  return { clusters, annex };
};

/**
 * Build the palace into `scene`. Returns { model, dispose }; also installs
 * `scene.userData.museumTick` (the streaming heartbeat, called from the
 * animation loop) and `scene.userData.palace` (for teardown).
 *
 * opts: { spawnClusterId, camera, controls, mobile, log, progress }
 * `progress` is called with a line of prose for the entry screen while the
 * plan is being generated.
 */
export const createPalaceMuseum = async (scene, tweets, opts = {}) => {
  const {
    spawnClusterId = null, camera, controls, renderer = null, mobile = false,
    log = () => {}, progress = () => {},
  } = opts;
  const tuning = mobile ? TUNING.mobile : TUNING.desktop;

  progress('Reading the archive…');
  await nextPaint();

  let analysis = null;
  try {
    const response = await fetch('/exports/insights/analysis_results.json');
    if (response.ok) analysis = await response.json();
  } catch (e) {
    // No analysis export: the whole archive hangs as one long annex wing.
  }

  const { clusters, annex } = joinClusters(analysis, tweets);
  progress(`Sorting ${tweets.length.toLocaleString()} saves into ${clusters.length} topics…`);
  await nextPaint();

  const plan = await runPlan(clusters, annex, progress);
  log(`Palace plan: ${plan.chunks.length} chunks, ${plan.tweetCount} saves hung`);
  progress('Opening the doors…');
  await nextPaint();

  const kit = createPalaceKit();
  const root = new THREE.Group();
  root.name = 'EndlessPalace';

  // The palace lights itself: warm skylight tone from above, umber bounce
  // from the floors, and a low directional key so opposing walls shade
  // differently and the trim relief reads. Fog covers chunk pop-in at the
  // streaming horizon.
  //
  // Both are deliberately dim. They exist to keep an unlit corner readable
  // rather than to light the building — a fill bright enough to do that job
  // washes out the lamp pool below and every room reads as the same flat box.
  root.add(new THREE.HemisphereLight(0xfff4e0, 0x6b5a48, 0.6));
  const key = new THREE.DirectionalLight(0xfff1dd, 0.26);
  key.position.set(3, 10, 2);
  root.add(key);
  const previousFog = scene.fog;
  scene.fog = new THREE.FogExp2(0xefe6d6, 0.014);

  // A metal with nothing to reflect is not a metal. Both lights above are dim
  // by design, so gilding and steel sections had no source to catch and shaded
  // as tinted plastic — the frame mouldings are the first thing in the building
  // that needs them to read. RoomEnvironment is a box of soft emissive panels
  // prefiltered into one cubemap: no extra draw calls, no shadow cost, and
  // every standard material in the palace picks it up.
  const previousEnvironment = scene.environment;
  let envTexture = null;
  if (renderer) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTexture;
    // Keep the room's own tone: the environment is there for specular, not to
    // relight the plaster, so it stays well under the hemisphere fill.
    scene.environmentIntensity = 0.35;
    pmrem.dispose();
  }

  scene.add(root);

  // Laylights are emissive planes: they read as bright but cast nothing, so
  // every room came out flat. A small pool of lamps follows the visitor
  // instead, parked under the ceiling of the nearest rooms. The pool is fixed
  // and permanently in the scene — adding or removing a light recompiles every
  // material, which a building that streams rooms cannot afford — so an unused
  // lamp is dimmed to zero rather than hidden.
  const lamps = [];
  for (let i = 0; i < tuning.LAMPS; i++) {
    const lamp = new THREE.PointLight(0xfff2dd, 0, 30, 1.85);
    root.add(lamp);
    lamps.push(lamp);
  }

  // --- chunk streaming state --------------------------------------------
  const live = new Map(); // chunk.id -> { chunk, group }
  const buildQueue = []; // chunks sorted nearest-first, one built per tick
  const quads = []; // every hung canvas mesh across live chunks
  let scanCursor = 0;
  let tickCount = 0;
  let disposed = false;

  const loadChunk = (chunk) => {
    if (live.has(chunk.id)) return;
    const group = buildChunk(chunk, kit);
    root.add(group);
    const entry = { chunk, group, selfLit: false };
    live.set(chunk.id, entry);
    group.traverse((node) => {
      if (node.userData.slot) {
        node.userData.chunkId = chunk.id;
        quads.push(node);
      }
      if (node.isLight) entry.selfLit = true; // centrepiece rooms light themselves
    });
  };

  // Bumping the paint stamp invalidates any media load still in flight for
  // this quad: by the time it lands the frame may hold a different save, or
  // no texture at all.
  const releaseQuadTexture = (quad) => {
    quad.userData.paint = (quad.userData.paint || 0) + 1;
    if (quad.material === kit.materials.linen) return;
    quad.material.map.dispose();
    quad.material.dispose();
    quad.material = kit.materials.linen;
  };

  const unloadChunk = (id) => {
    const entry = live.get(id);
    if (!entry) return;
    live.delete(id);
    for (let i = quads.length - 1; i >= 0; i--) {
      if (quads[i].userData.chunkId === id) {
        quads[i].userData.paint = (quads[i].userData.paint || 0) + 1;
        quads.splice(i, 1);
      }
    }
    disposeChunk(entry.group);
  };

  // Park the lamp pool on the nearest rooms that don't already light
  // themselves, brightest for the biggest spaces.
  const sweepLamps = (x, z) => {
    const nearest = [];
    for (const entry of live.values()) {
      if (entry.selfLit) continue;
      nearest.push({ entry, d: distToAabb(x, z, entry.chunk.aabb) });
    }
    nearest.sort((a, b) => a.d - b.d);
    lamps.forEach((lamp, i) => {
      const hit = nearest[i];
      if (!hit) {
        lamp.intensity = 0;
        return;
      }
      const c = hit.entry.chunk;
      const span = Math.max(c.x2 - c.x1, c.z2 - c.z1);
      lamp.position.set((c.x1 + c.x2) / 2, (c.height || 6.5) * 0.82, (c.z1 + c.z2) / 2);
      lamp.distance = Math.max(16, span * 1.8);
      lamp.intensity = Math.min(46, 15 + span * 2.1);
    });
  };

  const sweepChunks = (x, z) => {
    for (const [id, entry] of live) {
      if (distToAabb(x, z, entry.chunk.aabb) > tuning.UNLOAD_R) unloadChunk(id);
    }
    buildQueue.length = 0;
    for (const chunk of plan.chunks) {
      if (live.has(chunk.id)) continue;
      const d = distToAabb(x, z, chunk.aabb);
      if (d < tuning.LOAD_R) buildQueue.push({ chunk, d });
    }
    buildQueue.sort((a, b) => a.d - b.d);
  };

  const drawSlot = (slot, media) => drawContentCanvas(
    slot.tweet,
    { width: slot.w, height: slot.h },
    plan.metrics,
    tuning.PIXEL_BUDGET,
    media
  );

  // A save with a still gets painted twice: the text card immediately (a frame
  // is never blank while the network thinks about it), then the same card
  // again with the image once it lands. The stamp guards the gap — the quad
  // may have been released or its chunk unloaded by then.
  const paintMedia = (quad, texture, media) => {
    const stamp = quad.userData.paint;
    loadMedia(media.url, tuning.MEDIA_VARIANT).then((image) => {
      if (disposed || !image) return;
      if (quad.userData.paint !== stamp || quad.material.map !== texture) return;
      texture.image = drawSlot(quad.userData.slot, { image, isVideo: media.isVideo });
      texture.needsUpdate = true;
    });
  };

  const paintQuad = (quad) => {
    const slot = quad.userData.slot;
    const media = mediaForContent(slot.tweet);
    const ready = media && cachedMedia(media.url);
    const canvas = drawSlot(slot, ready ? { image: ready, isVideo: media.isVideo } : null);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
    // MeshBasic: cards read as evenly lit paintings and skip the lighting
    // cost across hundreds of live canvases. Fog still applies.
    quad.material = new THREE.MeshBasicMaterial({ map: texture });
    quad.userData.paint = (quad.userData.paint || 0) + 1;
    if (media && !ready) paintMedia(quad, texture, media);
  };

  const sweepTextures = (x, z) => {
    if (!quads.length) return;
    const want = [];
    const n = Math.min(TEX_SCAN_PER_TICK, quads.length);
    for (let i = 0; i < n; i++) {
      scanCursor = (scanCursor + 1) % quads.length;
      const quad = quads[scanCursor];
      const s = quad.userData.slot;
      const d = Math.hypot(s.x - x, s.z - z);
      if (quad.material === kit.materials.linen) {
        if (d < tuning.TEX_R) want.push({ quad, d });
      } else if (d > tuning.TEX_OUT) {
        releaseQuadTexture(quad);
      }
    }
    want.sort((a, b) => a.d - b.d);
    for (let i = 0; i < Math.min(TEX_CREATE_PER_TICK, want.length); i++) {
      paintQuad(want[i].quad);
    }
  };

  const tick = (cam) => {
    if (disposed) return;
    const { x, z } = cam.position;
    if (tickCount % CHUNK_CHECK_EVERY === 0) sweepChunks(x, z);
    tickCount++;
    if (buildQueue.length) loadChunk(buildQueue.shift().chunk);
    if (tickCount % 4 === 0) sweepLamps(x, z);
    sweepTextures(x, z);
  };

  // --- collision ----------------------------------------------------------
  // The visitor is a 0.4m circle in plan. Each live chunk's builder emitted
  // collider primitives (see toolkit.js); we push the camera out of each.
  // Two passes settle corners where one pushout lands inside a neighbour.
  const BODY_R = 0.4;

  const pushRect = (pos, [minX, minZ, maxX, maxZ]) => {
    const cx = Math.min(Math.max(pos.x, minX), maxX);
    const cz = Math.min(Math.max(pos.z, minZ), maxZ);
    const dx = pos.x - cx;
    const dz = pos.z - cz;
    const d2 = dx * dx + dz * dz;
    if (d2 >= BODY_R * BODY_R) return;
    if (d2 > 1e-9) {
      const d = Math.sqrt(d2);
      pos.x = cx + (dx / d) * BODY_R;
      pos.z = cz + (dz / d) * BODY_R;
      return;
    }
    // Inside the solid: exit through the nearest face.
    const exits = [
      { d: pos.x - minX, x: minX - BODY_R, z: pos.z },
      { d: maxX - pos.x, x: maxX + BODY_R, z: pos.z },
      { d: pos.z - minZ, x: pos.x, z: minZ - BODY_R },
      { d: maxZ - pos.z, x: pos.x, z: maxZ + BODY_R },
    ];
    const e = exits.reduce((a, b) => (b.d < a.d ? b : a));
    pos.x = e.x;
    pos.z = e.z;
  };

  const pushSeg = (pos, [ax, az, bx, bz], halfT) => {
    const r = BODY_R + halfT;
    const ex = bx - ax;
    const ez = bz - az;
    const len2 = ex * ex + ez * ez;
    const t = len2 > 0
      ? Math.min(1, Math.max(0, ((pos.x - ax) * ex + (pos.z - az) * ez) / len2))
      : 0;
    const px = ax + ex * t;
    const pz = az + ez * t;
    let dx = pos.x - px;
    let dz = pos.z - pz;
    const d2 = dx * dx + dz * dz;
    if (d2 >= r * r) return;
    let d = Math.sqrt(d2);
    if (d < 1e-5) {
      // On the line: push out along the segment normal.
      const inv = 1 / Math.sqrt(len2 || 1);
      dx = -ez * inv;
      dz = ex * inv;
      d = 1;
    }
    pos.x = px + (dx / d) * r;
    pos.z = pz + (dz / d) * r;
  };

  const pushCircle = (pos, [cx, cz, r]) => {
    const dx = pos.x - cx;
    const dz = pos.z - cz;
    const d2 = dx * dx + dz * dz;
    const keep = r + BODY_R;
    if (d2 >= keep * keep) return;
    const d = Math.sqrt(d2) || 1e-5;
    pos.x = cx + (dx / d) * keep;
    pos.z = cz + (dz / d) * keep;
  };

  const pushRing = (pos, [cx, cz, r], gaps) => {
    const dx = pos.x - cx;
    const dz = pos.z - cz;
    const d = Math.hypot(dx, dz);
    const keep = r - BODY_R;
    if (d <= keep) return;
    const angle = Math.atan2(dx, dz);
    const inGap = (gaps || []).some(([a0, a1]) =>
      (angle >= a0 && angle <= a1) ||
      (angle + 2 * Math.PI >= a0 && angle + 2 * Math.PI <= a1) ||
      (angle - 2 * Math.PI >= a0 && angle - 2 * Math.PI <= a1));
    if (inGap) return; // leaving through a doorway
    const dd = d || 1e-5;
    pos.x = cx + (dx / dd) * keep;
    pos.z = cz + (dz / dd) * keep;
  };

  const collide = (pos) => {
    if (disposed) return;
    for (let pass = 0; pass < 2; pass++) {
      for (const { chunk, group } of live.values()) {
        // A ring is a stay-inside constraint, and it only binds the visitor
        // while they are in the room that owns it. Applied from outside it
        // reels anyone in the chunk's whole load radius back into the drum —
        // the rotunda was yanking visitors out of rooms a corridor away the
        // moment a column nudged them off the axis of its doorway.
        const inFootprint = pos.x > chunk.x1 && pos.x < chunk.x2
          && pos.z > chunk.z1 && pos.z < chunk.z2;
        for (const c of group.userData.colliders) {
          // `soft` is furniture and water: knee-high things you walk straight
          // through rather than trace the edge of. Anything under 0.35m is
          // stepped over regardless.
          if (c.soft) continue;
          if (c.top !== undefined && c.top < 0.35) continue; // walk-over height
          if (c.rect) pushRect(pos, c.rect);
          else if (c.seg) pushSeg(pos, c.seg, c.t || 0);
          else if (c.circle) pushCircle(pos, c.circle);
          else if (c.ring && inFootprint) pushRing(pos, c.ring, c.gaps);
        }
      }
    }
  };

  // --- picking ------------------------------------------------------------
  // One ray, cast against the whole live building rather than just the hung
  // quads: a card you can see through a doorway from two rooms away is not a
  // card you can walk up to, and shouldn't be a card you can click either. The
  // nearest hit wins, and it only counts if it turned out to be a canvas.
  const raycaster = new THREE.Raycaster();
  raycaster.far = PICK_RANGE;

  const pick = (ndc) => {
    if (disposed || !camera) return null;
    raycaster.setFromCamera(ndc, camera);
    const hit = raycaster.intersectObject(root, true)[0];
    const slot = hit?.object.userData.slot;
    if (!slot) return null;
    return {
      tweet: slot.tweet,
      room: live.get(hit.object.userData.chunkId)?.chunk.label || null,
      distance: hit.distance,
    };
  };

  // --- spawn -------------------------------------------------------------
  const spawn = (spawnClusterId != null && plan.spawns.get(String(spawnClusterId)))
    || plan.spawns.get(null);
  if (camera && spawn) {
    camera.position.set(spawn.position.x, spawn.position.y, spawn.position.z);
    if (controls) {
      controls.target.set(spawn.target.x, spawn.target.y, spawn.target.z);
      controls.update();
    }
    sweepChunks(spawn.position.x, spawn.position.z);
    // Raise the near ground synchronously so the first frame isn't the void.
    buildQueue.splice(0, 3).forEach((q) => loadChunk(q.chunk));
    sweepLamps(spawn.position.x, spawn.position.z);
  }

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    for (const id of [...live.keys()]) unloadChunk(id);
    disposePalaceKit(kit);
    clearMediaCache();
    root.removeFromParent();
    scene.fog = previousFog;
    if (envTexture) {
      scene.environment = previousEnvironment;
      envTexture.dispose();
    }
    if (scene.userData.museumTick === tick) delete scene.userData.museumTick;
    if (scene.userData.museumCollide === collide) delete scene.userData.museumCollide;
    if (scene.userData.museumPick === pick) delete scene.userData.museumPick;
    delete scene.userData.palace;
  };

  scene.userData.museumTick = tick;
  scene.userData.museumCollide = collide;
  scene.userData.museumPick = pick;
  scene.userData.palace = { dispose, plan };

  return { model: root, dispose };
};
